import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    Output,
    OnDestroy,
    ViewChild,
} from '@angular/core';
import {
    AutocompleteComponent,
    BaseFieldComponent,
    NotificationService,
    TooltipTextComponent,
} from 'vcp-common-ui-lib';
import { IOrgCriteriaDTO, IOrgModel, OrgService } from '@libs/vc-org-lib';
import { translate } from '@jsverse/transloco';
import { isEmpty, isEqual } from 'lodash-es';
import { finalize, take } from 'rxjs';
import { TagsComponent } from '../tags-selector';

/**
 * Example usage of org filter:
 *
 * HTML:
 * <vc-org-filter [(org)]="orgFilter" (orgChange)="applyFilter()"></vc-org-filter>
 *
 * TS:
 * orgFilter!: IOrgModel | null;
 *
 * applyFilter(): void {
 *   console.log(orgFilter);
 * }
 */

@Component({
    selector: 'vc-org-filter',
    imports: [TagsComponent, AutocompleteComponent, TooltipTextComponent],
    templateUrl: './org-filter.component.html',
    styleUrl: './org-filter.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrgFilterComponent extends BaseFieldComponent<IOrgModel | null> implements OnDestroy {
    private readonly _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    private readonly _notificationService: NotificationService = inject(NotificationService);
    private readonly _orgService: OrgService = inject(OrgService);
    private _fetchDebouncer!: NodeJS.Timeout;
    orgs!: IOrgModel[];
    loading: boolean = false;

    /**
     * Input binding for the selected organization.
     * Updates the local `_org` value and emits change if different.
     */
    @Input()
    set org(org: IOrgModel | null) {
        if (!isEqual(org, this._org)) {
            this._org = org;
            this.orgChange.emit(this._org);
        }
    }

    // Getter for selected organization
    get org(): IOrgModel | null {
        return this._org;
    }

    // Internal backing field for the selected organization
    private _org: IOrgModel | null = null;

    /**
     * Label to show above the organization filter.
     * Default is localized string for "Organization".
     */
    @Input()
    label: string = translate('COMMON_UI.ORGANIZATION');

    /** 'is required' text */
    @Input()
    requiredText: string = translate('COMMON_UI.VALIDATION.FIELD_IS_REQUIRED');

    // Placeholder text for the input box
    @Input()
    placeholder!: string;

    @Input()
    required: boolean = false;

    @Input()
    minimalStyles: boolean = true;

    /**
     * Flag to indicate whether to display error message.
     */
    @Input()
    showErrorMessage: boolean = false;

    /**
     * Flag to indicate whether to display tags for filtering organizations.
     */
    @Input()
    showTags: boolean = false;

    @Input()
    showId: boolean = false;

    // Tags that should be excluded from the search results
    @Input()
    excludedTags!: string[];

    @Input()
    panelWidth!: string;

    @Input()
    disabled: boolean = false;

    /**
     * Output event emitter for changes in selected organization.
     * Supports two-way data binding with [(org)].
     */
    @Output()
    orgChange = new EventEmitter<IOrgModel | null>();

    @ViewChild('autocomplete')
    autocomplete!: AutocompleteComponent<IOrgModel>;

    override validate(): boolean {
        this.valid = this.autocomplete.validate();
        this._changeDetectorRef.markForCheck();
        return this.valid;
    }

    /**
     * Searches for organizations based on user input.
     * Applies any excluded tags if provided.
     * Updates `orgs` array with search results.
     *
     * @param orgString - The search string entered by the user
     */
    searchOrgs(orgString: string | null): void {
        this.orgs = [];

        if (isEmpty(orgString)) return;

        this.loading = true;
        const orgCriteria = {} as IOrgCriteriaDTO;
        orgCriteria.name = orgString as string;

        // Exclude tags from search if applicable
        if (!isEmpty(this.excludedTags)) {
            orgCriteria.excludeTags = this.excludedTags;
        }

        clearTimeout(this._fetchDebouncer);
        this._fetchDebouncer = setTimeout(() => {
            this._orgService
                .searchOrgs(orgCriteria)
                .pipe(
                    take(1),
                    finalize(() => {
                        this.loading = false;
                        this._changeDetectorRef.markForCheck();
                    })
                )
                .subscribe({
                    next: (orgs: IOrgModel[]) => {
                        this.orgs = orgs;
                    },
                    error: () => {
                        if (this.showErrorMessage) {
                            this._notificationService.error(translate('ORG_FILTER.UNABLE_TO_SEARCH_ORGANIZATIONS'));
                        }
                    },
                });
        }, 500);
    }

    /**
     * Function used to render organization name in the dropdown or UI.
     *
     * @param org - The organization object
     * @returns The name of the organization or empty string
     */
    orgRenderer = (org: IOrgModel): string => {
        if (!org?.name) {
            return '';
        }
        const idSuffix = this.showId ? ` (${org.id})` : '';
        return `${org.name}${idSuffix}`;
    };

    ngOnDestroy() {
        clearTimeout(this._fetchDebouncer);
    }
}
